import {
  BulkUpdateItemStatusRequest,
  CreateItemStatusRequest,
  ItemStatus,
  ItemStatusFilters,
  ItemStatusStatistics,
  ItemStatusTransitionValidation,
  ItemStatusValidation,
  PaginatedItemStatusResponse,
  ReorderItemStatusesRequest,
  UpdateItemStatusRequest,
} from "../types/admin";
import { ApiResponse } from "../types/common";

import apiClient from "./apiClient";

export interface ItemStatusService {
  // Read-only methods
  getAllItemStatuses(): Promise<ItemStatus[]>;
  getActiveItemStatuses(): Promise<ItemStatus[]>;
  getItemStatusById(id: number): Promise<ItemStatus>;
  getNextAllowedStatuses(currentStatusId: number): Promise<ItemStatus[]>;
  validateStatusTransition(
    currentStatusId: number,
    newStatusId: number,
  ): Promise<boolean>;

  // CRUD methods
  getItemStatusesWithFilters(
    filters: ItemStatusFilters,
  ): Promise<ApiResponse<PaginatedItemStatusResponse>>;
  getItemStatusDetail(id: number): Promise<ApiResponse<ItemStatus>>;
  createItemStatus(
    request: CreateItemStatusRequest,
  ): Promise<ApiResponse<ItemStatus>>;
  updateItemStatus(
    id: number,
    request: UpdateItemStatusRequest,
  ): Promise<ApiResponse<ItemStatus>>;
  deleteItemStatus(id: number): Promise<ApiResponse<boolean>>;
  toggleItemStatusActive(id: number): Promise<ApiResponse<boolean>>;
  bulkUpdateItemStatusActive(
    request: BulkUpdateItemStatusRequest,
  ): Promise<ApiResponse<boolean>>;
  reorderItemStatuses(
    request: ReorderItemStatusesRequest,
  ): Promise<ApiResponse<boolean>>;
  getItemStatusStatistics(): Promise<ApiResponse<ItemStatusStatistics>>;
  validateItemStatus(
    request: CreateItemStatusRequest,
  ): Promise<ApiResponse<ItemStatusValidation>>;
  validateItemStatusUpdate(
    request: UpdateItemStatusRequest,
  ): Promise<ApiResponse<ItemStatusValidation>>;
  validateItemStatusTransition(
    fromStatusId: number,
    toStatusId: number,
  ): Promise<ApiResponse<ItemStatusTransitionValidation>>;
}

class ItemStatusServiceImpl implements ItemStatusService {
  private readonly baseUrl = "admin/item-statuses";

  async getAllItemStatuses(): Promise<ItemStatus[]> {
    try {
      const response = await apiClient.get<ApiResponse<ItemStatus[]>>(
        `${this.baseUrl}/all`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error getting all item statuses:", error);
      throw new Error("Failed to get item statuses");
    }
  }

  async getActiveItemStatuses(): Promise<ItemStatus[]> {
    try {
      const response = await apiClient.get<ApiResponse<ItemStatus[]>>(
        `${this.baseUrl}/active`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error getting active item statuses:", error);
      throw new Error("Failed to get active item statuses");
    }
  }

  async getItemStatusById(id: number): Promise<ItemStatus> {
    try {
      const response = await apiClient.get<ApiResponse<ItemStatus>>(
        `${this.baseUrl}/${id}`,
      );
      if (!response.data.data) {
        throw new Error("Item status not found");
      }
      return response.data.data;
    } catch (error) {
      console.error("Error getting item status by ID:", error);
      throw new Error("Failed to get item status");
    }
  }

  async getNextAllowedStatuses(currentStatusId: number): Promise<ItemStatus[]> {
    try {
      const response = await apiClient.get<ApiResponse<ItemStatus[]>>(
        `${this.baseUrl}/${currentStatusId}/next-allowed`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error getting next allowed statuses:", error);
      throw new Error("Failed to get next allowed statuses");
    }
  }

  async validateStatusTransition(
    currentStatusId: number,
    newStatusId: number,
  ): Promise<boolean> {
    try {
      const response = await apiClient.get<ApiResponse<boolean>>(
        `${this.baseUrl}/validate-transition?fromStatusId=${currentStatusId}&toStatusId=${newStatusId}`,
      );
      return response.data.data || false;
    } catch (error) {
      console.error("Error validating status transition:", error);
      throw new Error("Failed to validate status transition");
    }
  }

  // CRUD methods
  async getItemStatusesWithFilters(
    filters: ItemStatusFilters,
  ): Promise<ApiResponse<PaginatedItemStatusResponse>> {
    try {
      const response = await apiClient.get<
        ApiResponse<PaginatedItemStatusResponse>
      >(this.baseUrl, { params: filters });
      return response.data;
    } catch (error) {
      console.error("Error getting item statuses with filters:", error);
      throw new Error("Failed to get item statuses");
    }
  }

  async getItemStatusDetail(id: number): Promise<ApiResponse<ItemStatus>> {
    try {
      const response = await apiClient.get<ApiResponse<ItemStatus>>(
        `${this.baseUrl}/${id}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting item status detail:", error);
      throw new Error("Failed to get item status detail");
    }
  }

  async createItemStatus(
    request: CreateItemStatusRequest,
  ): Promise<ApiResponse<ItemStatus>> {
    try {
      const response = await apiClient.post<ApiResponse<ItemStatus>>(
        this.baseUrl,
        request,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating item status:", error);
      throw new Error("Failed to create item status");
    }
  }

  async updateItemStatus(
    id: number,
    request: UpdateItemStatusRequest,
  ): Promise<ApiResponse<ItemStatus>> {
    try {
      const response = await apiClient.put<ApiResponse<ItemStatus>>(
        `${this.baseUrl}/${id}`,
        request,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating item status:", error);
      throw new Error("Failed to update item status");
    }
  }

  async deleteItemStatus(id: number): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.delete<ApiResponse<boolean>>(
        `${this.baseUrl}/${id}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting item status:", error);
      throw new Error("Failed to delete item status");
    }
  }

  async toggleItemStatusActive(id: number): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/${id}/toggle-active`,
      );
      return response.data;
    } catch (error) {
      console.error("Error toggling item status active:", error);
      throw new Error("Failed to toggle item status active");
    }
  }

  async bulkUpdateItemStatusActive(
    request: BulkUpdateItemStatusRequest,
  ): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/bulk-update-active`,
        request,
      );
      return response.data;
    } catch (error) {
      console.error("Error bulk updating item status active:", error);
      throw new Error("Failed to bulk update item status active");
    }
  }

  async reorderItemStatuses(
    request: ReorderItemStatusesRequest,
  ): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/reorder`,
        request,
      );
      return response.data;
    } catch (error) {
      console.error("Error reordering item statuses:", error);
      throw new Error("Failed to reorder item statuses");
    }
  }

  async getItemStatusStatistics(): Promise<ApiResponse<ItemStatusStatistics>> {
    try {
      const response = await apiClient.get<ApiResponse<ItemStatusStatistics>>(
        `${this.baseUrl}/statistics`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting item status statistics:", error);
      throw new Error("Failed to get item status statistics");
    }
  }

  async validateItemStatus(
    request: CreateItemStatusRequest,
  ): Promise<ApiResponse<ItemStatusValidation>> {
    try {
      const response = await apiClient.post<ApiResponse<ItemStatusValidation>>(
        `${this.baseUrl}/validate`,
        request,
      );
      return response.data;
    } catch (error) {
      console.error("Error validating item status:", error);
      throw new Error("Failed to validate item status");
    }
  }

  async validateItemStatusUpdate(
    request: UpdateItemStatusRequest,
  ): Promise<ApiResponse<ItemStatusValidation>> {
    try {
      const response = await apiClient.post<ApiResponse<ItemStatusValidation>>(
        `${this.baseUrl}/validate-update`,
        request,
      );
      return response.data;
    } catch (error) {
      console.error("Error validating item status update:", error);
      throw new Error("Failed to validate item status update");
    }
  }

  async validateItemStatusTransition(
    fromStatusId: number,
    toStatusId: number,
  ): Promise<ApiResponse<ItemStatusTransitionValidation>> {
    try {
      const response = await apiClient.get<
        ApiResponse<ItemStatusTransitionValidation>
      >(
        `${this.baseUrl}/validate-transition?fromStatusId=${fromStatusId}&toStatusId=${toStatusId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error validating item status transition:", error);
      throw new Error("Failed to validate item status transition");
    }
  }
}

export const itemStatusService = new ItemStatusServiceImpl();
export default itemStatusService;
